import { useState, useEffect, useCallback } from "react";
import { UserCheck, UserX } from "lucide-react";
import axios, { API } from "@/utils/api";

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, pendingRes, auditorsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/users/pending`),
        axios.get(`${API}/admin/auditors`),
        axios.get(`${API}/admin/clients`)
      ]);
      setUsers(usersRes.data);
      setPendingUsers(pendingRes.data);
      setAuditors(auditorsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateStatus = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { status });
      fetchData();
    } catch (error) {
      console.error("Error updating user status:", error);
    }
  };

  const handleAssignClient = async (clientId, auditorId) => {
    try {
      await axios.put(`${API}/admin/clients/${clientId}/assign`, { auditor_id: auditorId });
      fetchData();
    } catch (error) {
      console.error("Error assigning client:", error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel">
      <div className="page-header">
        <h2 className="page-title">Admin Panel</h2>
        <p className="page-subtitle">Manage auditors and client assignments</p>
      </div>

      <div className="tabs mb-lg">
        <button
          className={`tab ${activeTab === "pending" ? "active" : ""}`}
          onClick={() => setActiveTab("pending")}
          data-testid="tab-pending"
        >
          Pending Approvals ({pendingUsers.length})
        </button>
        <button
          className={`tab ${activeTab === "auditors" ? "active" : ""}`}
          onClick={() => setActiveTab("auditors")}
          data-testid="tab-auditors"
        >
          Auditors ({auditors.length})
        </button>
        <button
          className={`tab ${activeTab === "clients" ? "active" : ""}`}
          onClick={() => setActiveTab("clients")}
          data-testid="tab-clients"
        >
          Clients ({clients.length})
        </button>
      </div>

      {activeTab === "pending" && (
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">Pending Registrations</h4>
          </div>
          {pendingUsers.length === 0 ? (
            <div className="flex-center" style={{ padding: "var(--space-xxl)", color: "var(--text-muted)" }}>
              <div style={{ textAlign: "center" }}>
                <UserCheck size={48} strokeWidth={1} />
                <p className="mt-md">No pending registrations</p>
              </div>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map((user) => (
                    <tr key={user.id} data-testid={`pending-user-${user.id}`}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{new Date(user.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="flex gap-sm">
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: "var(--success)", color: "white" }}
                            onClick={() => handleUpdateStatus(user.id, "approved")}
                            data-testid={`approve-${user.id}`}
                          >
                            <UserCheck size={14} />
                            Approve
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: "var(--danger)", color: "white" }}
                            onClick={() => handleUpdateStatus(user.id, "rejected")}
                            data-testid={`reject-${user.id}`}
                          >
                            <UserX size={14} />
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "auditors" && (
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">All Auditors</h4>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Clients</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {auditors.map((auditor) => (
                  <tr key={auditor.id} data-testid={`auditor-${auditor.id}`}>
                    <td>{auditor.name}</td>
                    <td>{auditor.email}</td>
                    <td>
                      <span className={`badge badge-${auditor.status === "approved" ? "success" : auditor.status === "pending" ? "warning" : "danger"}`}>
                        {auditor.status}
                      </span>
                    </td>
                    <td>{auditor.client_count}</td>
                    <td>
                      {auditor.status !== "approved" && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleUpdateStatus(auditor.id, "approved")}
                        >
                          Approve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "clients" && (
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">All Clients</h4>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Location</th>
                  <th>Assigned Auditor</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id} data-testid={`client-row-${client.id}`}>
                    <td>{client.company_name}</td>
                    <td>{client.contact_name}</td>
                    <td>{client.city}, {client.country}</td>
                    <td>
                      {auditors.find(a => a.id === client.assigned_auditor_id)?.name || "Unassigned"}
                    </td>
                    <td>
                      <select
                        className="form-select"
                        style={{ width: "auto", height: "32px", padding: "0 8px" }}
                        value={client.assigned_auditor_id || ""}
                        onChange={(e) => handleAssignClient(client.id, e.target.value)}
                        data-testid={`assign-auditor-${client.id}`}
                      >
                        <option value="">-- Assign --</option>
                        {auditors.filter(a => a.status === "approved").map((auditor) => (
                          <option key={auditor.id} value={auditor.id}>
                            {auditor.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
